<div class="billing-page">
  <div class="billing-inner">

    <button type="button" class="link-back" (click)="goBack()">
      <mat-icon>arrow_back</mat-icon> Back to dashboard
    </button>

    <header class="page-head">
      <h1>{{ isProvider ? 'Billing' : 'Payment methods' }}</h1>
      <p>{{ isProvider ? 'Manage your plan and the cards on file.' : 'Cards you can use to pay for bookings.' }}</p>
    </header>

    <div class="alert error" *ngIf="errorMessage" role="alert">
      <mat-icon>error_outline</mat-icon><span>{{ errorMessage }}</span>
      <button type="button" class="alert-close" (click)="errorMessage = ''" aria-label="Dismiss"><mat-icon>close</mat-icon></button>
    </div>
    <div class="alert success" *ngIf="successMessage" role="status">
      <mat-icon>check_circle</mat-icon><span>{{ successMessage }}</span>
      <button type="button" class="alert-close" (click)="successMessage = ''" aria-label="Dismiss"><mat-icon>close</mat-icon></button>
    </div>

    <!-- Plan (providers) -->
    <section class="card plan-card" *ngIf="isProvider">
      <div class="card-head">
        <div>
          <div class="eyebrow">Current plan</div>
          <div class="plan-row">
            <span class="plan-name">{{ subscription ? planName : '—' }}</span>
            <span class="status-pill trial" *ngIf="subscription?.status === 'trialing'">Free trial</span>
            <span class="status-pill active" *ngIf="subscription?.status === 'active' && !subscription?.cancelAtPeriodEnd">Active</span>
            <span class="status-pill warn" *ngIf="subscription?.status === 'past_due'">Payment failed</span>
            <span class="status-pill muted" *ngIf="subscription?.cancelAtPeriodEnd">Cancels soon</span>
          </div>
        </div>
        <button type="button" class="btn primary" *ngIf="subscription && !hasPaidPlan" (click)="upgrade()">
          <mat-icon>bolt</mat-icon> Try Pro free
        </button>
      </div>

      <div class="plan-details" *ngIf="hasPaidPlan">
        <div class="detail" *ngIf="subscription?.status === 'trialing' && subscription?.trialEnd">
          <span class="label">{{ subscription?.cancelAtPeriodEnd ? 'Trial ends' : 'First charge' }}</span>
          <span class="value">{{ subscription?.trialEnd | date:'MMM d, y' }}</span>
        </div>
        <div class="detail" *ngIf="subscription?.status !== 'trialing' && subscription?.currentPeriodEnd">
          <span class="label">{{ subscription?.cancelAtPeriodEnd ? 'Access until' : 'Renews' }}</span>
          <span class="value">{{ subscription?.currentPeriodEnd | date:'MMM d, y' }}</span>
        </div>
        <div class="detail" *ngIf="subscription?.cardLast4">
          <span class="label">Billed to</span>
          <span class="value">{{ brandLabel(subscription?.cardBrand || '') }} •••• {{ subscription?.cardLast4 }}</span>
        </div>
      </div>

      <div class="plan-foot" *ngIf="hasPaidPlan">
        <ng-container *ngIf="!subscription?.cancelAtPeriodEnd">
          <span class="foot-note" *ngIf="!confirmCancel">You can cancel anytime. You'll keep access until the end of the current period.</span>
          <span class="foot-note danger" *ngIf="confirmCancel">Cancel your {{ planName }} plan? You'll keep access until the date above.</span>
          <div class="foot-actions">
            <button type="button" class="btn ghost" *ngIf="confirmCancel" (click)="confirmCancel = false">Keep plan</button>
            <button type="button" class="btn danger-ghost" (click)="cancelPlan()" [disabled]="planBusy">
              {{ planBusy ? 'Cancelling…' : (confirmCancel ? 'Yes, cancel' : 'Cancel plan') }}
            </button>
          </div>
        </ng-container>
        <ng-container *ngIf="subscription?.cancelAtPeriodEnd">
          <span class="foot-note">Your plan won't renew. Changed your mind?</span>
          <button type="button" class="btn primary" (click)="resumePlan()" [disabled]="planBusy">
            {{ planBusy ? 'Resuming…' : 'Resume plan' }}
          </button>
        </ng-container>
      </div>
    </section>

    <!-- Cards -->
    <section class="card">
      <div class="card-head">
        <div>
          <div class="eyebrow">Payment methods</div>
          <div class="card-sub">Your default card is used for {{ isProvider ? 'your plan' : 'bookings' }}.</div>
        </div>
        <button type="button" class="btn ghost" *ngIf="!showForm && paymentMethods.length" (click)="openForm()">
          <mat-icon>add</mat-icon> Add card
        </button>
      </div>

      <div class="skeleton-list" *ngIf="loading && !paymentMethods.length">
        <div class="sk"></div><div class="sk"></div>
      </div>

      <ul class="method-list" *ngIf="paymentMethods.length">
        <li class="method" *ngFor="let m of paymentMethods" [class.is-default]="m.isDefault">
          <span class="brand-chip" [attr.data-brand]="m.brand">{{ brandLabel(m.brand) }}</span>
          <div class="method-meta">
            <span class="digits">•••• {{ m.lastFourDigits }}</span>
            <span class="exp" [class.expired]="isExpired(m)">
              {{ isExpired(m) ? 'Expired' : 'Expires' }} {{ m.expiryMonth | number:'2.0' }}/{{ m.expiryYear }}
            </span>
          </div>
          <span class="default-tag" *ngIf="m.isDefault">Default</span>
          <div class="method-actions">
            <button type="button" class="btn text" *ngIf="!m.isDefault" (click)="setDefault(m.id)" [disabled]="busyMethodId === m.id">
              Make default
            </button>
            <button type="button" class="btn text danger" (click)="deletePaymentMethod(m.id)" [disabled]="busyMethodId === m.id">
              {{ confirmDeleteId === m.id ? 'Confirm remove' : 'Remove' }}
            </button>
          </div>
        </li>
      </ul>

      <div class="empty" *ngIf="!loading && !paymentMethods.length && !showForm">
        <div class="empty-icon"><mat-icon>credit_card</mat-icon></div>
        <p class="empty-title">No cards yet</p>
        <p class="empty-desc">Add a card to {{ isProvider ? 'start a plan' : 'book services' }} in one click.</p>
        <button type="button" class="btn primary" (click)="openForm()"><mat-icon>add</mat-icon> Add a card</button>
      </div>

      <div class="add-form" *ngIf="showForm">
        <div class="add-form-head">
          <span>New card</span>
          <button type="button" class="icon-btn" (click)="closeForm()" [disabled]="saving" aria-label="Close"><mat-icon>close</mat-icon></button>
        </div>
        <div class="element-wrap">
          <div class="element-skeleton" *ngIf="!elementReady">
            <div class="sk"></div>
            <div class="sk-row"><div class="sk"></div><div class="sk"></div></div>
          </div>
          <div #paymentElementRef class="payment-element" [class.visible]="elementReady"></div>
        </div>
        <div class="add-form-actions">
          <button type="button" class="btn ghost" (click)="closeForm()" [disabled]="saving">Cancel</button>
          <button type="button" class="btn primary" (click)="addPaymentMethod()" [disabled]="saving || !elementComplete">
            <span class="spinner" *ngIf="saving"></span>
            {{ saving ? 'Saving…' : 'Save card' }}
          </button>
        </div>
      </div>
    </section>

    <p class="secure-foot">
      <mat-icon>lock</mat-icon>
      Card details are encrypted and handled by Stripe. We never see or store your full card number.
    </p>
  </div>
</div>
